import React from "react"
import {
  Box, Flex, Heading, HStack, Stack, Text,
} from "@chakra-ui/react"
import { DioboxEvent } from "sharedTypes"
import { asDateWithTZ } from "utilities/strings"
import { DateFormat } from "utilities/constants"
import {
  differenceInDays, differenceInHours, differenceInMinutes, isValid,
} from "date-fns"
import { formatInTimeZone } from "date-fns-tz"
import { EventPhase, eventEnd } from "../phase"

const Digits = ({ value, unit }: { value: number, unit: string }) => (
  <Stack align="center" spacing={1.5}>
    <HStack spacing={1}>
      {String(value).padStart(2, "0").split("").map((digit, index) => (
        <Flex
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          position="relative"
          w={7}
          h={9}
          align="center"
          justify="center"
          bg="white"
          borderWidth={1}
          borderColor="gray.300"
          borderRadius="md"
          fontSize="2xl"
          fontWeight="semibold"
          lineHeight="none"
          overflow="hidden"
        >
          {digit}
          <Box position="absolute" insetX={0} top="50%" h="1px" bg="gray.100" />
        </Flex>
      ))}
    </HStack>
    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="gray.700">
      {unit}
    </Text>
  </Stack>
)

type Props = {
  event: DioboxEvent
  phase: EventPhase
  now: number
}

const unit = (value: number, name: string) => (value === 1 ? name : `${name}s`)

const Countdown = ({ event, phase, now }: Props) => {
  const start = new Date(event.startDate)
  if (!isValid(start)) {
    return null
  }

  if (phase === "day-of" && now >= start.getTime()) {
    return (
      <Text fontSize="md" fontWeight="semibold" color="green.600">Happening now</Text>
    )
  }

  if (phase === "post-event") {
    const days = Math.max(0, differenceInDays(now, eventEnd(start, event.endDate, event.timezone)))

    return (
      <HStack spacing={4} title="Days since event">
        <Digits value={days} unit={unit(days, "Day")} />
      </HStack>
    )
  }

  const days = differenceInDays(start, now)
  const hours = differenceInHours(start, now) % 24
  const minutes = differenceInMinutes(start, now) % 60
  const units = [[days, "Day"], [hours, "Hour"], [minutes, "Minute"]] as const

  return (
    <HStack spacing={4} align="flex-start" title="Countdown to event">
      {units.map(([value, name]) => <Digits key={name} value={value} unit={unit(value, name)} />)}
    </HStack>
  )
}

const Header = ({
  event, phase, now,
}: Props) => {
  const date = asDateWithTZ(event.startDate, event.timezone, DateFormat.DAY_OF_WEEK_MONTH_DAY_YEAR)
  const day = (at: number | string) => formatInTimeZone(new Date(at), event.timezone, "yyyy-MM-dd")
  const startDay = phase === "day-of" && day(now) === day(event.startDate)
  const location = [event.venue, event.addressLine1, event.city, event.country].filter(Boolean).join(", ")

  return (
    <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ md: "center" }} gap={6}>
      <Box minW={0} flex={1}>
        <Heading fontSize="3xl" fontWeight="semibold" letterSpacing="tight">{event.title}</Heading>
        <Text mt={1.5} color="gray.700">
          {[startDay ? `Today · ${date}` : date, location].filter(Boolean).join(" · ")}
        </Text>
      </Box>
      <Countdown event={event} phase={phase} now={now} />
    </Flex>
  )
}

export default Header
